import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

type Region = {
  id: string;
  title: string;
  description: string[];
  highlights: string[];
  image: string;
};

const regionFlagMap: Record<string, string> = {
  chad: "/images/regions/chad.svg",
  cameroon: "/images/regions/cameroon.svg",
  afghanistan: "/images/regions/afghanistan.svg",
  bangladesh: "/images/regions/bangladesh.svg",
  gaza: "/images/regions/palestine.svg",
  yemen: "/images/regions/yemen.svg",
  mauritania: "/images/regions/mauritania.svg",
  burundi: "/images/regions/burundi.svg",
};

export const Projects = () => {
  const { t } = useTranslation();
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);

  const regions = useMemo<Region[]>(() => {
    const rawItems = t("projects.items", { returnObjects: true }) as any[];
    const items = Array.isArray(rawItems) ? rawItems : [];

    return items.map((region) => {
      const description = Array.isArray(region.description)
        ? region.description
        : region.description
        ? [region.description]
        : [];
      const highlights = Array.isArray(region.highlights) ? region.highlights : [];

      return {
        id: region.id,
        title: region.title,
        description,
        highlights,
        image: regionFlagMap[region.id] || "/images/regions/chad.svg",
      };
    });
  }, [t]);

  return (
    <section id="regions" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.5 }}
          className="text-center md:text-left max-w-3xl mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
            {t("projects.title")}
          </h2>
          <p className="mt-4 text-lg text-neutral-600">
            {t("projects.subtitle")}
          </p>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
          {regions.map((region, index) => {
            const isExpanded = expandedRegion === region.id;
            const contentId = `${region.id}-details`;
            const preview = region.description[0];

            return (
              <motion.article
                key={region.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.04 }}
                viewport={{ once: true, amount: 0.4 }}
                className="flex flex-col rounded-3xl border border-neutral-200 bg-neutral-50/70 p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpandedRegion((prev) => (prev === region.id ? null : region.id))
                  }
                  className="flex w-full items-center gap-4 text-left"
                  aria-expanded={isExpanded}
                  aria-controls={contentId}
                  aria-label={
                    isExpanded
                      ? `${region.title} – ${t("projects.collapse")}`
                      : `${region.title} – ${t("projects.expand")}`
                  }
                >
                  <div className="h-16 w-24 overflow-hidden rounded-2xl border border-neutral-200 bg-white md:h-20 md:w-28">
                    <img
                      src={region.image}
                      alt={region.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-1 items-center justify-between gap-3">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-turquoise block">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h3 className="mt-2 text-xl font-semibold text-neutral-900 md:text-2xl">
                        {region.title}
                      </h3>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-brand-turquoise transition-transform duration-300 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                      aria-hidden
                    />
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                      id={contentId}
                    >
                      <div className="pt-4 space-y-4 text-sm md:text-base leading-relaxed text-neutral-700">
                        {region.description.map((paragraph, paragraphIndex) => (
                          <p key={paragraphIndex}>{paragraph}</p>
                        ))}

                        {region.highlights.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {region.highlights.map((highlight, highlightIndex) => (
                              <span
                                key={highlightIndex}
                                className="inline-flex items-center gap-2 rounded-full border border-brand-turquoise/30 bg-white px-3 py-1 text-xs font-medium text-brand-turquoise md:text-sm"
                              >
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-turquoise" aria-hidden />
                                {highlight}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        <div className="pt-2">
                          <a
                            href="#contact"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-turquoise bg-brand-turquoise/10 px-4 py-2 text-sm font-semibold text-brand-turquoise transition hover:bg-brand-turquoise hover:text-white"
                          >
                            {t("projects.contact_cta")}
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!isExpanded && preview ? (
                  <p className="mt-4 text-sm text-neutral-600">{preview}</p>
                ) : null}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
